// Google Trends geography codes, derived from a campaign's targeting. Demand is
// regional — the same category can read as rising in one state and falling
// nationally, so the geo matters. Trends supports countries and regions, not
// cities; the city is only used for the separate events lookup.

// StateType enum (full names) to the subdivision code Trends expects.
const STATE_CODES = {
  ALABAMA: 'AL', ALASKA: 'AK', AMERICAN_SAMOA: 'AS', ARIZONA: 'AZ', ARKANSAS: 'AR',
  CALIFORNIA: 'CA', COLORADO: 'CO', CONNECTICUT: 'CT', DELAWARE: 'DE',
  DISTRICT_OF_COLUMBIA: 'DC', FLORIDA: 'FL', GEORGIA: 'GA', GUAM: 'GU', HAWAII: 'HI',
  IDAHO: 'ID', ILLINOIS: 'IL', INDIANA: 'IN', IOWA: 'IA', KANSAS: 'KS', KENTUCKY: 'KY',
  LOUISIANA: 'LA', MAINE: 'ME', MARYLAND: 'MD', MASSACHUSETTS: 'MA', MICHIGAN: 'MI',
  MINNESOTA: 'MN', MISSISSIPPI: 'MS', MISSOURI: 'MO', MONTANA: 'MT', NEBRASKA: 'NE',
  NEVADA: 'NV', NEW_HAMPSHIRE: 'NH', NEW_JERSEY: 'NJ', NEW_MEXICO: 'NM', NEW_YORK: 'NY',
  NORTH_CAROLINA: 'NC', NORTH_DAKOTA: 'ND', NORTHERN_MARIANA_ISLANDS: 'MP', OHIO: 'OH',
  OKLAHOMA: 'OK', OREGON: 'OR', PENNSYLVANIA: 'PA', PUERTO_RICO: 'PR',
  RHODE_ISLAND: 'RI', SOUTH_CAROLINA: 'SC', SOUTH_DAKOTA: 'SD', TENNESSEE: 'TN',
  TEXAS: 'TX', UTAH: 'UT', VERMONT: 'VT', VIRGIN_ISLAND: 'VI', VIRGINIA: 'VA',
  WASHINGTON: 'WA', WEST_VIRGINIA: 'WV', WISCONSIN: 'WI', WYOMING: 'WY',
};

// Canadian provinces share the enum but need a different country prefix.
const PROVINCE_CODES = {
  ALBERTA: 'AB', BRITISH_COLUMBIA: 'BC', MANITOBA: 'MB', NEW_BRUNSWICK: 'NB',
  NEWFOUNDLAND_AND_LABRADOR: 'NL', NOVA_SCOTIA: 'NS', ONTARIO: 'ON',
  PRINCE_EDWARD_ISLAND: 'PE', QUEBEC: 'QC', SASKATCHEWAN: 'SK',
};

const titleCase = (value) =>
  String(value)
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

function fromGeoTargets(targets) {
  const out = [];
  (targets || []).forEach((target) => {
    if (target?.location) out.push(target.location);
    (target?.circles || []).forEach((circle) => {
      if (circle?.location) out.push(circle.location);
    });
    (target?.polygons || []).forEach((polygon) => {
      if (polygon?.location) out.push(polygon.location);
    });
  });
  return out;
}

// Targeting groups first — that is where the campaign actually buys. They are
// often empty at planning time though (line items are optional under Has Media
// Plan Strategy), so retail stores are the fallback.
function collectLocations({ lineItems, attributionTargets }) {
  const fromTargeting = [];
  (lineItems || []).forEach((lineItem) => {
    (lineItem?.targetingLocations || []).forEach((group) => {
      fromTargeting.push(...fromGeoTargets(group?.geoTargets));
    });
  });

  if (fromTargeting.length > 0) return fromTargeting;

  return fromGeoTargets(attributionTargets);
}

const mostCommon = (values) => {
  const counts = new Map();
  values.forEach((v) => counts.set(v, (counts.get(v) || 0) + 1));
  let best = null;
  let bestCount = 0;
  counts.forEach((count, value) => {
    if (count > bestCount) {
      best = value;
      bestCount = count;
    }
  });
  return best;
};

// Returns { geo, city }, either of which may be null. Narrows to a region only
// when every location sits in the same one — a multi-state campaign has no single
// demand curve, so it falls back to the country rather than picking one state.
export function deriveTrendsGeo({ lineItems, attributionTargets }) {
  const locations = collectLocations({ lineItems, attributionTargets });
  if (locations.length === 0) return { geo: null, city: null };

  const states = [...new Set(locations.map((l) => l?.state).filter(Boolean))];
  const countries = [...new Set(locations.map((l) => l?.country).filter(Boolean))];

  const canadian = states.length > 0 && states.every((s) => PROVINCE_CODES[s]);
  const countryPrefix = canadian ? 'CA' : 'US';

  const singleCountry =
    countries.length === 1 && countries[0].length === 2 ? countries[0].toUpperCase() : null;

  // The country is the fallback whenever no subdivision resolves, not only when
  // no state was given. Defaulting an unmapped state to 'US' reads a different
  // demand curve than the campaign actually buys against.
  let geo = singleCountry || countryPrefix;
  if (states.length === 1) {
    const code = canadian ? PROVINCE_CODES[states[0]] : STATE_CODES[states[0]];
    if (code) geo = `${countryPrefix}-${code}`;
  }

  // State name included: Google needs it to disambiguate duplicate city names.
  const cities = locations.map((l) => l?.city).filter(Boolean);
  const city = mostCommon(cities);
  const cityState = city
    ? locations.find((l) => l?.city === city && l?.state)?.state
    : null;

  return {
    geo,
    city: city ? (cityState ? `${city}, ${titleCase(cityState)}` : city) : null,
  };
}
